import { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { RubricDragContext } from '../Context';
import { isRubricDrag, getRubricDropTarget } from '../../utils/rubricDrop';

const accentColor = (isDark) => (isDark ? '#4fc3f7' : '#1976d2');

/**
 * Zone de dépôt des rubriques et canevas glissés depuis le panneau gauche.
 * dropZoneProps se pose sur le conteneur ; les emplacements d'insertion sont ses descendants
 * marqués data-rubric-slot="<id de rubrique>".
 * dropIndex : rang d'insertion survolé (0..nombre de rubriques), null hors survol.
 */
export function useRubricDropZone() {
  const { draggedRubricLabel, dropRubric } = useContext(RubricDragContext);
  const [dropIndex, setDropIndex] = useState(null);

  useEffect(() => {
    if (!draggedRubricLabel) setDropIndex(null);
  }, [draggedRubricLabel]);

  const onDragOver = useCallback((event) => {
    if (!isRubricDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDropIndex(getRubricDropTarget(event.currentTarget, event.clientY).index);
  }, []);

  // dragleave remonte aussi des descendants : on ne réinitialise qu'en sortant réellement du conteneur
  const onDragLeave = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (
      event.clientX <= rect.left || event.clientX >= rect.right ||
      event.clientY <= rect.top || event.clientY >= rect.bottom
    ) {
      setDropIndex(null);
    }
  }, []);

  const onDrop = useCallback((event) => {
    if (!isRubricDrag(event)) return;
    event.preventDefault();
    setDropIndex(null);
    dropRubric(getRubricDropTarget(event.currentTarget, event.clientY).beforeRubricId);
  }, [dropRubric]);

  return {
    draggedRubricLabel,
    dropIndex,
    dropZoneProps: { onDragOver, onDragLeave, onDrop }
  };
}

/**
 * Trait d'insertion, à placer dans un emplacement en position relative.
 * offset : distance entre le bord de l'emplacement et le milieu de l'espace qui le sépare du voisin.
 */
export function RubricDropIndicator({ placement, offset, label, isDark }) {
  const color = accentColor(isDark);

  return (
    <Box sx={{
      position: 'absolute',
      left: 0,
      right: 0,
      [placement === 'after' ? 'bottom' : 'top']: -offset,
      height: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      pointerEvents: 'none'
    }}>
      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: -1.5,
        height: 3,
        borderRadius: 2,
        backgroundColor: color,
        boxShadow: `0 0 6px ${color}`
      }} />
      {label && (
        <Typography
          component="span"
          sx={{
            position: 'relative',
            maxWidth: '80%',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: color,
            color: isDark ? '#1a252f' : '#ffffff',
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

// Contour de dépôt quand le document ne contient encore aucune rubrique
export function RubricDropOverlay({ label, isDark }) {
  const color = accentColor(isDark);

  return (
    <Box sx={{
      position: 'absolute',
      inset: 8,
      zIndex: 3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `2px dashed ${color}`,
      borderRadius: 2,
      backgroundColor: isDark ? 'rgba(26, 37, 47, 0.8)' : 'rgba(255, 255, 255, 0.8)',
      pointerEvents: 'none'
    }}>
      <Typography sx={{
        maxWidth: '80%',
        px: 2,
        py: 1,
        borderRadius: 1,
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '0.9rem',
        backgroundColor: color,
        color: isDark ? '#1a252f' : '#ffffff'
      }}>
        {label ? `Déposer « ${label} » ici` : 'Déposer ici'}
      </Typography>
    </Box>
  );
}
